import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { BookOpen, ScanLine, ShieldCheck } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

/** Split-screen frame for sign-in pages: a navy brand panel and a clean form column. */
export function AuthLayout({ title, subtitle, children, footer, audience = 'patron' }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; audience?: 'patron' | 'staff' }) {
  const points =
    audience === 'staff'
      ? [
          { icon: ScanLine, text: 'Scan-anywhere circulation desk' },
          { icon: BookOpen, text: 'Koha-compatible catalogue and MARC21' },
          { icon: ShieldCheck, text: 'Granular permissions and a full audit trail' },
        ]
      : [
          { icon: BookOpen, text: 'Search the whole collection in seconds' },
          { icon: ScanLine, text: 'Place holds and renew loans anywhere' },
          { icon: ShieldCheck, text: 'Your reading history stays private' },
        ];
  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <aside className="theme-dark relative hidden overflow-hidden bg-midnight p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-sapphire-500/30 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 right-[-8rem] h-[30rem] w-[30rem] rounded-full bg-ice-500/15 blur-3xl" aria-hidden="true" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.07]" aria-hidden="true">
          <defs>
            <pattern id="auth-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M48 0H0V48" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#auth-grid)" />
        </svg>
        <Link to="/" className="relative rounded-lg" aria-label="Lumina home">
          <Logo size={40} tone="onDark" />
        </Link>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight text-white">Every book, every reader, beautifully in order.</h2>
          <ul className="mt-8 space-y-4">
            {points.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-navy-200">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15">
                  <Icon className="h-[1.125rem] w-[1.125rem] text-sapphire-200" aria-hidden="true" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-navy-400">Lumina Library Management</p>
      </aside>
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-4 lg:justify-end lg:p-6">
          <Link to="/" className="rounded-lg lg:hidden" aria-label="Lumina home">
            <Logo size={32} />
          </Link>
          <div className="flex items-center gap-1">
            <LanguageSwitcher compact />
            <ThemeToggle />
          </div>
        </div>
        <main id="main" tabIndex={-1} className="flex flex-1 items-center justify-center px-5 pb-12 outline-none">
          <div className="w-full max-w-md">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-ink-muted">{subtitle}</p>}
            <div className="mt-8 rounded-card bg-surface p-6 shadow-raised sm:p-8">{children}</div>
            {footer && <div className="mt-6 text-center text-sm text-ink-muted">{footer}</div>}
          </div>
        </main>
      </div>
    </div>
  );
}
